<template>
    <div :class="['tournament-position-card', medalClass, { 'rank-1': rank === 1 }]">
        <span class="big-rank">{{ rank }}°</span>
        <div class="card-content">
            <div class="identity-row">
                <img class="avatar should-blur" :src="avatarSrc" alt="" />
                <div class="identity">
                    <p class="name-txt should-blur">
                        <template v-if="showBrokerAccount && brokerAccountId">
                            {{ brokerAccountId }}
                        </template>
                        <template v-else-if="!showBrokerAccount && showIbNumber && ibNumber">
                            {{ ibNumber }}
                        </template>
                        <template v-else>
                            {{ name.substring(0, 16) + (name.length > 16 ? '...' : '') }}
                        </template>
                    </p>
                    <span class="place-chip">
                        <img v-if="showMedalImages && medalSrc" :src="medalSrc" class="place-chip__medal" alt="" />
                        <span class="place-chip__text">{{ getPlaceText(rank) }}</span>
                    </span>
                </div>
            </div>
            <p v-if="scoreValue !== null && scoreValue !== undefined" class="score-line">
                <b>{{ scoreLabel }}</b> {{ scoreValue }}
            </p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'TournamentPositionCard',
    props: {
        name: {
            type: String,
            required: true
        },
        rank: {
            type: Number,
            required: true,
            default: 1
        },
        countryCode: {
            type: String,
            required: false,
            default: null
        },
        brokerAccountId: {
            type: String,
            required: false,
            default: null
        },
        showBrokerAccount: {
            type: Boolean,
            default: false
        },
        showIbNumber: {
            type: Boolean,
            default: false
        },
        ibNumber: {
            type: [String, Number],
            default: null
        },
        medalType: {
            type: String,
            default: "default"
        },
        showMedalImages: {
            type: Boolean,
            default: true
        },
        profilePicture: {
            type: String,
            default: null
        },
        scoreLabel: {
            type: String,
            default: null
        },
        scoreValue: {
            type: String,
            default: null
        }
    },
    computed: {
        medalClass() {
            switch (this.rank) {
                case 1:
                    return 'gold';
                case 2:
                    return 'silver';
                case 3:
                    return 'bronze';
                default:
                    return '';
            }
        },

        avatarSrc() {
            return this.profilePicture || 'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png';
        },

        medalSrc() {
            switch (this.rank) {
                case 1:
                    return require('@/assets/tournament-facelift/medal-gold.svg');
                case 2:
                    return require('@/assets/tournament-facelift/medal-silver.svg');
                case 3:
                    return require('@/assets/tournament-facelift/medal-bronze.svg');
                default:
                    return null;
            }
        },
    },
    methods: {

        normalizedUiLocale() {
            const raw = String(this.$i18n.locale || 'en').toLowerCase();
            if (raw.startsWith('zh-hant')) return 'zh-hant';
            return raw.split('-')[0];
        },

        englishOrdinal(rank) {
            const suffixes = ['th', 'st', 'nd', 'rd'];
            const v = rank % 100;
            const suf = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
            return `${rank}${suf}`;
        },
        getPlaceText(rank) {
            const loc = this.normalizedUiLocale();

            if (loc === 'th') {
                return this.$t('place_TournamentPositionCard', { rank });
            }


            if (loc === 'vi') {
                if (rank === 1) return this.$t('place_TournamentPositionCard_1');
                if (rank === 2) return this.$t('place_TournamentPositionCard_2');
                if (rank === 3) return this.$t('place_TournamentPositionCard_3');
                return this.$t('place_TournamentPositionCard_rank', { rank });
            }


            const rankOnlyPhraseLocales = ['ja', 'ko', 'zh', 'zh-hant', 'hi', 'id', 'ru'];
            if (rankOnlyPhraseLocales.includes(loc)) {
                return this.$t('place_TournamentPositionCard', { rank });
            }

            if (loc === 'en') {
                return `${this.englishOrdinal(rank)} ${this.$t('place_TournamentPositionCard')}`;
            }

            if (loc === 'es' || loc === 'pt') {
                return `${rank}º ${this.$t('place_TournamentPositionCard')}`;
            }

            return `${rank} ${this.$t('place_TournamentPositionCard')}`;
        },
    },
}
</script>

<style lang="scss" scoped>
.tournament-position-card {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    border-radius: 15px;
    padding: 10px 20px;
    min-height: 104px;
    flex: 1 1 0;
    min-width: 0;
    font-family: "Avenir", sans-serif;

    &.gold {
        background: linear-gradient(180deg, #FFECBB 0%, #FFFAEF 100%);
        min-height: 124px;

        .big-rank {
            color: #FFE39F;
        }

        .place-chip__text {
            color: #FFC123;
        }
    }

    &.silver {
        background: linear-gradient(180deg, #DEDEDE 0%, #FBFBFB 100%);

        .big-rank {
            color: #D7D7D7;
        }

        .place-chip__text {
            color: #ABABAB;
        }
    }

    &.bronze {
        background: linear-gradient(180deg, #F2C380 0%, #FFF8EE 100%);

        .big-rank {
            color: #F2C584;
        }

        .place-chip__text {
            color: #D38851;
        }
    }

    .big-rank {
        position: absolute;
        right: -10px;
        top: 50%;
        transform: translateY(-50%);
        font-family: "Aeonik", "Avenir", sans-serif;
        font-weight: 900;
        font-size: 86px;
        letter-spacing: -1.7px;
        line-height: 1;
        pointer-events: none;
        user-select: none;
    }

    .card-content {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        gap: 5px;
        width: 100%;
        min-width: 0;
    }

    .identity-row {
        display: flex;
        align-items: center;
        gap: 9px;
        min-width: 0;
    }

    .avatar {
        width: 52px;
        height: 52px;
        min-width: 52px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid var(--v-fullWhite-base, #fff);
    }

    .identity {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        min-width: 0;
    }

    .name-txt {
        font-size: 16px;
        font-weight: 800;
        color: #1B1B1B;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }

    .place-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background-color: var(--v-fullWhite-base, #fff);
        border-radius: 18px;
        padding: 3px 8px;
        margin-top: 2px;

        &__medal {
            width: 17px;
            height: 18px;
        }

        &__text {
            font-size: 14px;
            font-weight: 800;
            white-space: nowrap;
        }
    }

    .score-line {
        font-family: "Aeonik", "Avenir", sans-serif;
        font-size: 18px;
        color: #3B3B3B;
        margin: 0;
        white-space: nowrap;

        b {
            font-weight: 700;
        }
    }
}

/* Mobile styles — cards render inside the top carousel */
@media screen and (max-width: 768px) {
    .tournament-position-card {
        min-height: 120px;
        width: 100%;
        margin: 0 !important;

        &.rank-1 {
            min-height: 130px;
            margin: 0 !important;
        }

        .big-rank {
            font-size: 64px;
        }

        .name-txt {
            font-size: 14px;
        }

        .score-line {
            font-size: 15px;
        }
    }
}
</style>
